import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    Alert,
    Divider,
    Box,
    useMediaQuery,
    useTheme,
    Grid,
    Chip,
    Skeleton,
    IconButton,
    Paper,
    LinearProgress,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { usePermissao } from '../../security/usePermissao';
import { usePerfilPlano } from '../../security/usePerfilPlano';
import { alpha } from '@mui/material/styles';

import { useBootstrap } from '../../context/BootstrapContext';
import { useInference } from '../../hooks/useInference';
import {
    ALVO,
    alvoDeAgua,
    diluicaoSeAplica,
    isSoPresenca,
    secoesDoResultado,
} from '../../utils/predicao';

import InfoItem from './ProcessDialog/InfoItem';
import DesktopResults from './ProcessDialog/DesktopResults';
import MobileResults from './ProcessDialog/MobileResults';

const safeLog10 = (value) => {
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? Math.log10(num) : null;
};

export default function ProcessDialog({
    open,
    onClose,
    experiment,
    onProcessado,
}) {
    const { bootstrap } = useBootstrap();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const configOnlyPresence = bootstrap?.config?.onlyPresence === true;

    // Bacterias do protocolo do experimento (calibrationSets), na ordem dele. Protocolo
    // sem calibrationSets: as duas historicas, para amostra antiga nao mudar.
    const bacteriasDoProtocolo = React.useMemo(() => {
        const protoId = experiment?.protocolId || experiment?.protocolo?.idProtocolo || experiment?.protocolo?.idProtocol;
        const proto = (bootstrap?.protocolos || []).find(p => p.id === protoId || p._id === protoId);
        const seen = new Set();
        const lista = [];
        for (const cs of proto?.calibrationSets || []) {
            if (cs.bacteriaId && !seen.has(cs.bacteriaId)) {
                seen.add(cs.bacteriaId);
                lista.push({ bacteriaId: cs.bacteriaId, bacteriaNome: cs.bacteriaNome || null });
            }
        }
        return lista.length
            ? lista
            : [
                  { bacteriaId: null, bacteriaNome: 'Coliformes totais' },
                  { bacteriaId: null, bacteriaNome: 'Escherichia coli' },
              ];
    }, [bootstrap?.protocolos, experiment?.protocolId, experiment?.protocolo]);

    const { pode } = usePermissao();
    const { resultadoQualitativo, statusGeral } = usePerfilPlano();
    const canProcessExperiments = pode('amostra.processarIA');

    const {
        labResult,
        predicao,
        isSubmitting,
        error,
        processExperiment,
        dilutionFactor,
    } = useInference(experiment, open, onProcessado);

    const hasAttemptedAutoProcess = useRef(false);

    useEffect(() => {
        if (open) {
            hasAttemptedAutoProcess.current = false;
        }
    }, [open, experiment?.id]);

    useEffect(() => {
        if (
            open &&
            experiment?.id &&
            canProcessExperiments &&
            !predicao &&
            !isSubmitting &&
            !hasAttemptedAutoProcess.current
        ) {
            hasAttemptedAutoProcess.current = true;
            processExperiment();
        }
    }, [
        open,
        experiment?.id,
        canProcessExperiments,
        predicao,
        isSubmitting,
        processExperiment,
    ]);

    const canSubmit = !!experiment?.id;

    // Copiar o ID do experimento. Mesmo padrao do ExportDataDialog: feedback de
    // ~1,8s e falha silenciosa — `navigator.clipboard` exige contexto seguro, e
    // se nao houver o usuario ainda consegue selecionar o texto na tela.
    const [idCopiado, setIdCopiado] = useState(false);

    const copiarExperimentId = async () => {
        if (!experiment?.id) return;
        try {
            await navigator.clipboard.writeText(String(experiment.id));
            setIdCopiado(true);
            setTimeout(() => setIdCopiado(false), 1800);
        } catch {
            /* clipboard indisponivel — texto continua selecionavel */
        }
    };


    // Linhas do resultado pelo leitor unico (utils/predicao secoesDoResultado): cada
    // bacteria do protocolo casada com o alvo do modelo pelo NOME, modelo e laboratorio
    // na mesma unidade — o mesmo numero do card de Amostras e da pagina de coleta.
    const isPresenceMode =
        configOnlyPresence || resultadoQualitativo || isSoPresenca(predicao);

    const secoes = React.useMemo(
        () =>
            secoesDoResultado({
                predicao,
                bacterias: bacteriasDoProtocolo,
                labResult,
                diluicao: dilutionFactor,
            }).map((s) => ({
                ...s,
                predLog: isPresenceMode ? null : s.predLog,
                labLog: s.labComparavel ? safeLog10(s.labValor) : null,
                agua: alvoDeAgua(s.alvo),
            })),
        [predicao, bacteriasDoProtocolo, labResult, dilutionFactor, isPresenceMode]
    );
    const hasPresenceData = secoes.some((s) => s.presenca !== undefined);
    const usaDiluicao = diluicaoSeAplica(predicao);

    // Metodo e base normativa dos cards do rodape, pelos alvos do resultado: agua
    // (coliformes/E. coli) segue a Portaria 888; leite, a IN 76 do MAPA.
    const referencia = secoes.some((s) => s.agua)
        ? { metodo: 'NMP/100 mL', detalhe: 'Limite de detecção: < 1,8 NMP/100 mL', norma: 'Portaria GM/MS Nº 888/2021' }
        : secoes.some((s) => s.alvo === ALVO.MESOFILOS_TOTAIS)
          ? { metodo: 'UFC/mL', detalhe: 'Contagem Bacteriana Total (CBT)', norma: 'IN MAPA Nº 76/2018 (leite cru refrigerado)' }
          : null;

    const apiErrorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;

    const resultsProps = {
        isPresenceMode,
        hasPresenceData,
        isSubmitting,
        secoes,
    };

    const isPending = isSubmitting || (!hasPresenceData && !error);
    const secoesComAlvo = secoes.filter((s) => s.alvo);
    const isSafe = secoesComAlvo.length > 0 && secoesComAlvo.every((s) => s.presenca === false);

    const formatDialogDate = (timestamp) => {
        if (!timestamp) return '—';
        return new Date(parseInt(timestamp) * 1000).toLocaleString('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
        });
    };

    const hasResults = predicao !== null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            scroll="paper"
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 2 : 3,
                    m: isMobile ? 1 : 4,
                    width: isMobile ? 'calc(100% - 16px)' : undefined,
                    maxHeight: isMobile ? 'calc(100% - 32px)' : undefined,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                },
            }}
        >
            <DialogTitle
                sx={{
                    p: isMobile ? 2 : 3,
                    bgcolor: 'primary.main',
                    color: '#ffffff',
                }}
            >
                <Stack spacing={isMobile ? 1.5 : 2}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: isMobile ? 36 : 40,
                                    height: isMobile ? 36 : 40,
                                    borderRadius: '12px',
                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    backdropFilter: 'blur(4px)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                }}
                            >
                                <Lucide.Droplets
                                    size={isMobile ? 20 : 24}
                                    strokeWidth={2.5}
                                    color="#0ea5e9"
                                />
                            </Box>
                            <Typography
                                variant={isMobile ? 'subtitle1' : 'h6'}
                                fontWeight={800}
                                textTransform="uppercase"
                                letterSpacing={0.5}
                            >
                                Resultado da Amostra
                            </Typography>
                        </Box>

                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{
                                color: '#ffffff',
                                opacity: 0.8,
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                },
                                mr: isMobile ? -1 : -1.5,
                            }}
                        >
                            <Lucide.X />
                        </IconButton>
                    </Stack>

                    <Stack
                        direction={isMobile ? 'column' : 'row'}
                        spacing={isMobile ? 1 : 4}
                        alignItems={isMobile ? 'flex-start' : 'center'}
                        sx={{ opacity: 1 }} // Aumentado para 1 para garantir contraste total
                    >
                        {/* SN: FONTE PADRONIZADA E MAIOR */}
                        <Typography
                            variant="body2"
                            fontWeight={800}
                            sx={{
                                fontSize: '0.95rem',
                                color: '#ffffff',
                            }}
                        >
                            SN: {experiment?.serialNumber || 'N/A'}
                        </Typography>

                        <Stack
                            direction={isMobile ? 'column' : 'row'}
                            spacing={isMobile ? 1 : 3}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <Lucide.Calendar
                                    size={18}
                                    color="#4caf50"
                                />
                                <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    sx={{ fontSize: '0.9rem' }}
                                >
                                    Início:{' '}
                                    {formatDialogDate(experiment?.startDate)}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Lucide.Calendar
                                    size={18}
                                    color="#ff9800"
                                />
                                <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    sx={{ fontSize: '0.9rem' }}
                                >
                                    Fim: {formatDialogDate(experiment?.endDate)}
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>

                    {/* ID do experimento — em linha propria porque e um UUID de 36
                        caracteres e disputaria espaco com o SN e as datas. E o
                        primeiro dado que o suporte e o dono do modelo pedem quando
                        um resultado sai divergente, e ate agora nao aparecia em
                        lugar nenhum deste modal. */}
                    {experiment?.id && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ minWidth: 0 }}
                        >
                            <Lucide.Fingerprint
                                size={16}
                                color="#90caf9"
                                style={{ flexShrink: 0 }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily:
                                        'Consolas, "Courier New", monospace',
                                    fontSize: '0.78rem',
                                    color: 'rgba(255,255,255,0.85)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    // Selecionavel a mao caso o clipboard falhe.
                                    userSelect: 'all',
                                }}
                                title={experiment.id}
                            >
                                {experiment.id}
                            </Typography>
                            <Tooltip
                                title={idCopiado ? 'Copiado!' : 'Copiar ID'}
                            >
                                <IconButton
                                    size="small"
                                    onClick={copiarExperimentId}
                                    aria-label="Copiar ID do experimento"
                                    sx={{
                                        color: idCopiado
                                            ? '#81c784'
                                            : '#ffffff',
                                        opacity: idCopiado ? 1 : 0.75,
                                        flexShrink: 0,
                                        '&:hover': {
                                            opacity: 1,
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                        },
                                    }}
                                >
                                    {idCopiado ? (
                                        <Lucide.Check size={16} />
                                    ) : (
                                        <Lucide.Copy size={16} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}
                </Stack>
            </DialogTitle>

            {isSubmitting && (
                <LinearProgress
                    sx={{
                        height: 3,
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        '& .MuiLinearProgress-bar': { bgcolor: 'primary.light' },
                    }}
                />
            )}

            <DialogContent
                dividers
                sx={{ p: isMobile ? 2 : 3, bgcolor: 'background.paper' }}
            >
                <Stack spacing={3}>
                    {isSubmitting && (
                        <Box
                            sx={{
                                p: isMobile ? 2 : 2.5,
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: alpha(theme.palette.primary.main, 0.25),
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                gap: isMobile ? 2 : 2.5,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <CircularProgress size={isMobile ? 40 : 52} thickness={3.5} color="primary" />
                            </Box>

                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                                    <Lucide.Activity size={14} color={theme.palette.primary.main} />
                                    <Typography
                                        variant="overline"
                                        fontWeight={800}
                                        color="primary.main"
                                        sx={{ letterSpacing: 1.5 }}
                                    >
                                        Analisando Amostra
                                    </Typography>
                                </Stack>

                                <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight={800} color="text.primary">
                                    Processando modelo de IA...
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    O modelo RBF está calculando a contagem microbiológica
                                    a partir dos dados do sensor. Isso pode levar alguns segundos.
                                </Typography>

                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.5 }}>
                                    {[0, 1, 2].map((i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                width: 7,
                                                height: 7,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main',
                                                '@keyframes bioai-bounce': {
                                                    '0%, 80%, 100%': { transform: 'scale(0.55)', opacity: 0.35 },
                                                    '40%': { transform: 'scale(1)', opacity: 1 },
                                                },
                                                animation: `bioai-bounce 1.4s ease-in-out ${i * 0.18}s infinite`,
                                            }}
                                        />
                                    ))}
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                        Aguardando resposta da API de inferência
                                    </Typography>
                                </Stack>
                            </Box>
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error">
                            <Typography variant="subtitle2" fontWeight="bold">
                                Falha ao processar API:
                            </Typography>
                            <Box>{apiErrorMsg || 'Erro desconhecido.'}</Box>
                        </Alert>
                    )}

                    <Box>
                        {hasResults && !isSubmitting && statusGeral && (
                            <Box sx={{ mt: 2, mb: 4 }}>
                                <Box
                                    sx={{
                                        mb: 1.5,
                                        p: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: isPending
                                            ? alpha(theme.palette.text.primary, 0.04)
                                            : isSafe
                                              ? alpha(theme.palette.success.main, 0.12)
                                              : alpha(theme.palette.error.main, 0.12),
                                        border: '1px solid',
                                        borderColor: isPending
                                            ? 'divider'
                                            : isSafe
                                              ? alpha(theme.palette.success.main, 0.35)
                                              : alpha(theme.palette.error.main, 0.35),
                                        borderRadius: 3,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={2}
                                    >
                                        <Box
                                            sx={{
                                                bgcolor: isPending
                                                    ? 'action.disabledBackground'
                                                    : isSafe
                                                      ? 'success.main'
                                                      : 'error.main',
                                                color: '#ffffff',
                                                borderRadius: '50%',
                                                p: 1.25,
                                                display: 'flex',
                                            }}
                                        >
                                            {isPending ? (
                                                <Skeleton
                                                    variant="circular"
                                                    width={24}
                                                    height={24}
                                                    animation="wave"
                                                />
                                            ) : isSafe ? (
                                                <Lucide.CircleCheck size={24} strokeWidth={2.5} />
                                            ) : (
                                                <Lucide.TriangleAlert
                                                    size={24}
                                                    strokeWidth={2.5}
                                                />
                                            )}
                                        </Box>
                                        <Box sx={{ textAlign: 'left' }}>
                                            <Typography
                                                variant="caption"
                                                fontWeight="900"
                                                textTransform="uppercase"
                                                display="block"
                                                color="text.secondary"
                                                sx={{ letterSpacing: 1 }}
                                            >
                                                Status Geral
                                            </Typography>
                                            {isPending ? (
                                                <Skeleton
                                                    variant="rounded"
                                                    width={160}
                                                    height={24}
                                                    animation="wave"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            ) : (
                                                <Typography
                                                    variant="h6"
                                                    fontWeight="800"
                                                    color={
                                                        isSafe
                                                            ? 'success.main'
                                                            : 'error.main'
                                                    }
                                                    lineHeight={1.2}
                                                >
                                                    {isSafe
                                                        ? 'Qualidade Segura'
                                                        : 'Risco Microbiológico'}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            </Box>
                        )}

                        {isMobile ? (
                            <MobileResults {...resultsProps} />
                        ) : (
                            <DesktopResults {...resultsProps} />
                        )}

                        {/* CARTÕES DE METODOLOGIA E BASE NORMATIVA */}
                        {referencia && (
                        <Box mt={2}>
                            <Grid container spacing={2} alignItems="stretch">
                                <Grid item xs={12} sm={6}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            borderRadius: 2,
                                            bgcolor: alpha(
                                                theme.palette.primary.main,
                                                0.02
                                            ),
                                            border: '1px solid',
                                            borderColor: alpha(
                                                theme.palette.primary.main,
                                                0.15
                                            ),
                                            boxShadow:
                                                '0px 4px 12px rgba(0, 0, 0, 0.05)',
                                            height: '100%',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <Lucide.Microscope
                                            size={28}
                                            color={theme.palette.primary.main}
                                        />
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                fontWeight={600}
                                            >
                                                Método de Referência
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                                color="text.primary"
                                            >
                                                {referencia.metodo}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                sx={{ mt: 0.25 }}
                                            >
                                                {referencia.detalhe}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            borderRadius: 2,
                                            bgcolor: alpha(
                                                theme.palette.primary.main,
                                                0.02
                                            ),
                                            border: '1px solid',
                                            borderColor: alpha(
                                                theme.palette.primary.main,
                                                0.15
                                            ),
                                            boxShadow:
                                                '0px 4px 12px rgba(0, 0, 0, 0.05)',
                                            height: '100%',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <Lucide.Scale
                                            size={28}
                                            color={theme.palette.primary.main}
                                        />
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                fontWeight={600}
                                            >
                                                Base Normativa
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                                color="text.primary"
                                            >
                                                {referencia.norma}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>
                        )}

                        {labResult && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    mt: 1,
                                    display: 'block',
                                    textAlign: 'right',
                                }}
                            >
                                Análise Lab:{' '}
                                {new Date(
                                    labResult.analysisDate
                                ).toLocaleDateString('pt-BR')}
                            </Typography>
                        )}
                    </Box>

                    {/* BLOCO CONDICIONAL PARA FATOR DE DILUIÇÃO E COMENTÁRIO */}
                    {(!isPresenceMode || experiment?.comentario) && (
                        <Box mt={2}>
                            <Divider sx={{ mb: 3 }} />
                            <Grid container spacing={2}>
                                {!isPresenceMode && (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={experiment?.comentario ? 6 : 12}
                                    >
                                        <InfoItem
                                            icon={<Lucide.FlaskConical />}
                                            label="Fator de Diluição"
                                            color="warning"
                                            value={
                                                !usaDiluicao
                                                    ? 'Não se aplica (análise sem diluição)'
                                                    : dilutionFactor > 0
                                                      ? `10⁻${dilutionFactor}`
                                                      : 'Pura (Sem diluição)'
                                            }
                                        />
                                    </Grid>
                                )}
                                {experiment?.comentario && (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={!isPresenceMode ? 6 : 12}
                                    >
                                        <InfoItem
                                            icon={<Lucide.MessageSquare />}
                                            label="Comentário"
                                            color="action"
                                            value={experiment.comentario}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    justifyContent: 'flex-end',
                }}
            >
                {canProcessExperiments && (
                    <Button
                        onClick={() => processExperiment()}
                        disabled={!canSubmit || isSubmitting}
                        variant="contained"
                        sx={{ minWidth: 120, minHeight: 40 }}
                    >
                        {isSubmitting
                            ? 'Processando...'
                            : predicao
                              ? 'Reprocessar'
                              : 'Processar'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
